const _isPrerenderContext: boolean =
  typeof window !== 'undefined' &&
  (window.env?.IS_PRERENDER === 'true' ||
    (typeof navigator !== 'undefined' &&
      navigator.userAgent.toLowerCase().includes('prerender')));

/** Returns true when the page is being rendered by Prerender.io. */
export const isPrerenderContext = (): boolean => _isPrerenderContext;

/**
 * Wait for network to become idle using PerformanceObserver.
 * Tracks ALL resources: fetch, XHR, images, scripts, fonts, CSS.
 */
const waitForNetworkIdle = (idleMs = 1500, maxMs = 15_000): Promise<void> =>
  new Promise((resolve) => {
    if (typeof PerformanceObserver === 'undefined') {
      setTimeout(resolve, idleMs);
      return;
    }

    let lastActivity = Date.now();
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      observer.disconnect();
    };

    const maxTimer = setTimeout(() => {
      finish();
      resolve();
    }, maxMs);

    const observer = new PerformanceObserver(() => {
      lastActivity = Date.now();
    });

    try {
      observer.observe({ entryTypes: ['resource'] });
    } catch {
      clearTimeout(maxTimer);
      setTimeout(resolve, idleMs);
      return;
    }

    const check = () => {
      if (done) return;
      if (Date.now() - lastActivity >= idleMs) {
        clearTimeout(maxTimer);
        finish();
        resolve();
      } else {
        setTimeout(check, 200);
      }
    };

    setTimeout(check, 500);
  });

const waitForImages = (timeoutMs = 10_000): Promise<void> => {
  const imgs = Array.from(document.querySelectorAll<HTMLImageElement>('img'));
  const pending = imgs.filter((img) => !img.complete);
  if (!pending.length) return Promise.resolve();

  return new Promise<void>((resolve) => {
    let remaining = pending.length;
    const done = () => {
      if (--remaining <= 0) resolve();
    };
    for (const img of pending) {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }
    setTimeout(resolve, timeoutMs);
  });
};

// Budget covers the whole page, since prerender.io's ~20s timer starts at
// request time — so SSR and hydration count against it, not just the waits.
const PRERENDER_TOTAL_BUDGET_MS = 18_000;

// `performance.now()` is ms since navigation start.
const remainingBudget = () =>
  Math.max(0, PRERENDER_TOTAL_BUDGET_MS - performance.now());

let _signaling = false;

/**
 * Signal Prerender.io that the page is ready for snapshotting.
 * Waits for network idle + all images. No-op outside prerender context.
 * Idempotent: only the first call runs; subsequent calls return immediately.
 * Always signals — never leaving the flag unset, which would stall to a 504.
 */
export const signalPrerenderReady = async (): Promise<void> => {
  if (!_isPrerenderContext || _signaling) return;
  _signaling = true;

  try {
    // `.catch` on `waits`, not the race: if the deadline wins, this is still
    // pending and a later rejection would be unhandled.
    const waits = (async () => {
      await waitForNetworkIdle(1500, Math.min(9000, remainingBudget()));
      await waitForImages(Math.min(4000, remainingBudget()));
      // Second pass: catch images added to DOM during the first wait cycle
      if (remainingBudget() > 500) {
        await waitForNetworkIdle(500, Math.min(3000, remainingBudget()));
        await waitForImages(Math.min(2000, remainingBudget()));
      }
    })().catch(() => {
      // Partial snapshot beats never signalling.
    });

    const hardDeadline = new Promise<void>((resolve) => {
      setTimeout(resolve, remainingBudget());
    });

    await Promise.race([waits, hardDeadline]);
  } finally {
    window.prerenderReady = true;
  }
};
